import { Dialog, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { Save, X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../../global/forms/Button';

const useDeleteReportClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 560,
        gap: 16,
        padding: '40px 40px 24px 40px',
        color: theme.colors.neutral[1],
        '& > span:first-child': { ...theme.fonts.h4 },
        '& > span:nth-child(2)': {
            ...theme.fonts.label,
            '& > span': { ...theme.fonts.labelSemiBold },
        },
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 16,
    },
});

/**
 * Dialog to delete a report.
 * This component does not delete reports on the backend by its own, it only informs actions to parent components.
 *
 * @param props.loading indicates a loading state for both loading the report data or the delete action
 * @param props.title report title
 * @param props.open set the dialog display state
 * @param props.onClose callback triggered when the user clicks on the background, if not provided, onCancel is called
 * @param props.onCancel callback triggered when the user clicks on the cancel button
 * @param props.onDelete callback triggered when the user clicks on the delete button
 */
export const DeleteReport = (props: {
    loading?: boolean;
    title: string;
    open: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onDelete?: () => void;
}) => {
    const classes = useDeleteReportClasses();
    const { t, Trans } = usePrefixTranslation('report', { report: 'dialogs.DeleteReport' });

    if (props.loading)
        return (
            <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
                <Skeleton variant='text' width='50%' />
                <Skeleton variant='text' />
                <div className={classes.buttons}>
                    <Skeleton variant='rect' width='200px' height='50px' />
                    <Skeleton variant='rect' width='200px' height='50px' />
                </div>
            </Dialog>
        );

    return (
        <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
            <span>{t('title')}</span>
            <span>
                <Trans i18nKey='subtitle' values={{ name: props.title }} components={{ bold: <span /> }} />
            </span>
            <div className={classes.buttons}>
                <Button theme='warning' disabled={props.loading} onClick={props.onDelete}>
                    <Save /> {t('button-delete')}
                </Button>
                <Button theme='secondary' disabled={props.loading} onClick={props.onCancel}>
                    <X /> {t('button-cancel')}
                </Button>
            </div>
        </Dialog>
    );
};
